// src/pages/Dashboard.tsx (or JoyOrderDashboardTemplate.tsx)
import * as React from "react";
import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Alert from "@mui/joy/Alert";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { AddRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header"; // Adjust path as needed
import Sidebar from "../components/VendorSidebar"; // Adjust path as needed
import { useGetInvoiceEntriesQuery } from "../services/invoices/InvoiceApiSlice";
import VendorOrderTable from "../components/VendorOrderTable";

export default function DashboardTable() {
  // --- ALL HOOKS AT THE VERY TOP ---
  const navigate = useNavigate();

  const { data: invoiceEntriesData, isLoading, isFetching, isError, error, refetch } = useGetInvoiceEntriesQuery();

  const [dashboardTitle, setDashboardTitle] = React.useState<string>(`Invoices Overview`);

  const handleAddEntry = () => {
    const ROLE = localStorage.getItem("userRole");
    if (ROLE === "vendor") {
      navigate("/add-new-entry");
    } else if (ROLE === "employee") {
      navigate("/employee/add_new_entry");
    }
  };

  if (isLoading || isFetching) {
    return (
      <CssVarsProvider disableTransitionOnChange>
        <CssBaseline />
        <Box sx={{ display: "flex", minHeight: "92dvh", background: 'white' }}>
          {/* <Header /> */}
          <Sidebar />
          <Box
            component="main"
            sx={{
              flex: 1,
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size="lg" />
            <Typography sx={{ ml: 2 }}>Loading dashboard data...</Typography>
          </Box>
        </Box>
      </CssVarsProvider>
    );
  }

  // Error State UI
  if (isError) {
    let errorMessage = "An unknown error occurred while loading dashboard data.";
    if (error) {
      if ("status" in error) {
        // FetchBaseQueryError
        const errData = error.data as { message?: string }; // Type assertion
        errorMessage = `Error ${error.status}: ${errData?.message || JSON.stringify(error.data) || "Failed to load data."}`;
      } else if ("message" in error) {
        // SerializedError or standard Error
        errorMessage = (error as Error).message;
      }
    }
    return (
      <CssVarsProvider disableTransitionOnChange>
        <CssBaseline />
        <Box sx={{ display: "flex", minHeight: "92dvh", background: 'white' }}>
          <Sidebar />
          <Box component="main" sx={{ flex: 1, p: 3 }}>
            <Alert startDecorator={<ReportProblemIcon />} variant="soft" color="danger" sx={{ alignItems: "flex-start", textAlign: "left", m: 2 }}>
              <div>
                <Typography fontWeight="md" mt={0.25}>
                  Error Loading Dashboard
                </Typography>
                <Typography fontSize="sm" sx={{ opacity: 0.8, whiteSpace: "pre-wrap" }}>
                  {errorMessage}
                </Typography>
                <Button size="sm" variant="solid" color="danger" onClick={refetch} sx={{ mt: 1 }}>
                  Try Again
                </Button>
              </div>
            </Alert>
          </Box>
        </Box>
      </CssVarsProvider>
    );
  }

  // Success State / Main Content UI (if not loading and not error)
  return (
    <CssVarsProvider disableTransitionOnChange>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "92dvh", background: 'white' }}>
        {/* <Header /> */}
        <Sidebar />
        <Box
          component="main"
          className="MainContent"
          sx={{
            px: { xs: 2, md: 3 },
            pt: {
              xs: "calc(12px + var(--Header-height))",
              sm: "calc(12px + var(--Header-height))",
              md: 3,
            },
            pb: { xs: 2, sm: 2, md: 3 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            gap: 2, // Increased gap for better spacing between title/button and table
          }}
        >
          <Box
            sx={{
              display: "flex",
              // mb: 1, // Removed direct margin, gap on parent Box will handle spacing
              gap: 1,
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "start", sm: "center" },
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Typography level="h2" component="h1" className="heading-2" sx={{ fontSize: '20px !important' }}>
              {dashboardTitle}
            </Typography>
            <Button color="primary" onClick={handleAddEntry} startDecorator={<AddRounded />} size="sm" className="heading-4">
              Add New Entry
            </Button>
          </Box>

          {invoiceEntriesData.data.length > 0 ? (
            <VendorOrderTable invoiceEntries={invoiceEntriesData.data} />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexGrow: 1,
              }}
            >
              <Typography sx={{ p: 2, textAlign: "center" }}>No invoice data available to display.</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </CssVarsProvider>
  );
}
